import { useState } from "react";
import { Link } from "react-router-dom";
import { blogs } from "../data/blog";
import { motion, AnimatePresence } from "framer-motion";

export default function BlogList() {
  const [visible, setVisible] = useState(4);

  const comingSoon = {
    title: "New Blog Coming Soon",
    date: "Stay tuned",
    excerpt:
      "I’m brewing my next deep-dive into real-world data engineering, architecture, pipelines, or governance, dropping soon!",
    image: "/images/blogs/coming-soon.jpg",
  };

  const allBlogs = [comingSoon, ...blogs];
  const visibleBlogs = allBlogs.slice(0, visible);

  return (
    <div className="max-w-7xl mx-auto py-14 px-4 sm:px-6 lg:px-8">
      {/* === Section Header === */}
      <div className="text-center mb-10">
        <motion.img
          src="/neuralbrew.png"
          alt="NeuralBrew Logo"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto w-16 sm:w-20 mb-3 opacity-95 drop-shadow-[0_0_8px_rgba(0,224,183,0.25)]"
        />

        <h1 className="text-3xl sm:text-4xl font-bold text-white">
          NeuralBrew Blog
        </h1>
        <p className="text-slate-400 mt-2 text-sm sm:text-base">
          Freshly brewed thoughts on data engineering, architecture, and systems.
        </p>
      </div>

      {/* === Blog Grid === */}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr">
        <AnimatePresence>
          {visibleBlogs.map((blog, index) =>
            blog.slug ? (
              // --- Published Blog ---
              <motion.div
                key={blog.slug}
                className="h-full"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Link to={`/blog/${blog.slug}`} className="block h-full">
                  <div
                    className="h-full bg-white/5 p-6 rounded-2xl border border-white/10
                               hover:border-brand-teal/40 hover:bg-white/[0.07]
                               transition-all duration-300 flex flex-col justify-between
                               min-h-[19rem] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(0,224,183,0.25)]"
                  >
                    {blog.image && (
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="rounded-xl mb-4 w-full h-40 object-cover"
                      />
                    )}

                    <div className="flex flex-col justify-between flex-grow text-center">
                      <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-white mb-1">
                          {blog.title}
                        </h2>
                        <p className="text-slate-400 text-xs mb-2">{blog.date}</p>
                        <p className="text-slate-300 text-sm italic max-w-sm mx-auto leading-relaxed">
                          {blog.excerpt}
                        </p>
                      </div>

                      <div className="mt-5 flex justify-center">
                        <span
                          className="inline-block bg-brand-teal/20 text-brand-teal px-4 py-1.5
                                     rounded-full text-xs font-semibold border border-brand-teal/30
                                     animate-glow"
                        >
                          Brewed 🧠
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>

            ) : (
              // --- Coming Soon Card ---
              <motion.div
                key={`coming-soon-${index}`}
                className="h-full relative flex flex-col justify-center items-center text-center
                           bg-gradient-to-br from-brand-blue/10 via-brand-violet/10 to-brand-teal/10
                           border border-white/10 rounded-2xl shadow-[0_0_25px_rgba(75,95,255,0.15)]
                           hover:shadow-[0_0_35px_rgba(75,95,255,0.25)] transition-all duration-500
                           p-8 min-h-[19rem] overflow-hidden hover:-translate-y-1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <h2 className="text-lg sm:text-xl font-semibold text-white mb-1">
                  {blog.title}
                </h2>
                <p className="text-slate-400 text-xs mb-2">{blog.date}</p>
                <p className="text-slate-300 text-sm italic max-w-sm mx-auto leading-relaxed">
                  {blog.excerpt}
                </p>
                <div className="mt-5 flex justify-center">
                  <span
                    className="inline-block bg-brand-teal/20 text-brand-teal px-4 py-1.5
                               rounded-full text-xs font-semibold border border-brand-teal/30
                               animate-glow"
                  >
                    Brewing ☕
                  </span>
                </div>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>

      {/* === Load More Button === */}
      {visible < allBlogs.length && (
        <div className="flex justify-center mt-10">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setVisible((prev) => prev + 4)}
            className="px-6 py-2 rounded-full bg-gradient-to-r from-brand-teal to-brand-blue
                       text-white font-semibold shadow-md hover:shadow-[0_0_20px_rgba(75,95,255,0.35)]
                       transition"
          >
            Load More
          </motion.button>
        </div>
      )}
    </div>
  );
}
